import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { FileDown, FileText, Loader2, Search, Table2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Monatsauswertungen als PDF (F11 Arbeitszeitnachweis, F12 Arbeitszeit und Projekte)

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August',
  'September', 'Oktober', 'November', 'Dezember'];

export const REPORT_KINDS = {
  timesheet: {
    label: 'Arbeitszeitnachweis',
    hint: 'A4 hoch · Arbeits- und Pausenzeiten, Konto, Abwesenheiten, Prüfung ArbZG',
    icon: FileText,
  },
  reconciliation: {
    label: 'Arbeitszeit und Projekte',
    hint: 'A4 quer · Arbeitszeit je Tag gegen die Buchungen, eine Spalte je Auftrag',
    icon: Table2,
  },
};

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
export const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

// Standard: Vormonat (der laufende Monat ist noch nicht abgeschlossen)
const defaultMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return monthKey(d);
};

const monthOptions = () => {
  const list = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 24; i += 1) {
    list.push(monthKey(d));
    d.setMonth(d.getMonth() - 1);
  }
  return list;
};

const TONE_CLASSES = {
  ok: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  wait: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  bad: 'bg-red-500/15 text-red-400 border-red-500/30',
};

// PDF/ZIP laden und speichern; Fehlertext aus der Blob-Antwort lesen
export async function downloadMonthlyReport(api, { month, kind, userIds = [], output = 'pdf' }) {
  const params = new URLSearchParams({ month, kind, output });
  if (userIds.length) params.append('user_ids', userIds.join(','));
  try {
    const response = await api.get(`/reports/monthly/pdf?${params.toString()}`, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const isZip = (response.headers['content-type'] || '').includes('zip');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([response.data], { type: isZip ? 'application/zip' : 'application/pdf' }));
    link.download = match ? match[1] : `Auswertung_${month}.${isZip ? 'zip' : 'pdf'}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    return true;
  } catch (error) {
    let message = 'PDF konnte nicht erstellt werden';
    if (error.response?.data instanceof Blob) {
      try {
        const body = JSON.parse(await error.response.data.text());
        if (typeof body.detail === 'string') message = body.detail;
      } catch { /* Standardtext */ }
    }
    toast.error(message);
    return false;
  }
}

export default function MonthlyReportsDialog({ open, onOpenChange, api }) {
  const [month, setMonth] = useState(defaultMonth);
  const [kind, setKind] = useState('timesheet');
  const [output, setOutput] = useState('pdf');
  const [persons, setPersons] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadPersons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/monthly/persons?month=${month}`);
      const list = res.data.persons || [];
      setPersons(list);
      // Vorauswahl: alle mit Einträgen im Monat
      setSelected(new Set(list.filter((p) => p.has_entries).map((p) => p.id)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Personen konnten nicht geladen werden');
      setPersons([]);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  }, [api, month]);

  useEffect(() => {
    if (open) loadPersons();
  }, [open, loadPersons]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return persons.filter((p) => {
      if (filter === 'final' && !p.final) return false;
      if (filter === 'open' && p.final) return false;
      if (q && !p.name.toLowerCase().includes(q) && !(p.personnel_number || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [persons, filter, search]);

  const weekHeads = persons.length ? persons[0].weeks.map((w) => w.kw) : [];
  const selectedList = persons.filter((p) => selected.has(p.id));
  const preliminaryCount = selectedList.filter((p) => !p.final).length;
  const allVisibleSelected = visible.length > 0 && visible.every((p) => selected.has(p.id));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((p) => next.delete(p.id));
      else visible.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const handleCreate = async () => {
    if (!selectedList.length) {
      toast.error('Bitte mindestens eine Person auswählen');
      return;
    }
    setBusy(true);
    const ok = await downloadMonthlyReport(api, {
      month, kind, output: selectedList.length > 1 ? output : 'pdf', userIds: selectedList.map((p) => p.id),
    });
    setBusy(false);
    if (ok) toast.success(`${REPORT_KINDS[kind].label} ${monthLabel(month)} erstellt`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Monatsauswertungen (PDF)</DialogTitle>
          <DialogDescription>
            Je Person eine Seite im CTI-Design. Wochen, die noch nicht genehmigt sind, erscheinen mit dem Vermerk „vorläufig“.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Monat</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="bg-input border-border" data-testid="monthly-report-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-72">
                {monthOptions().map((m) => (
                  <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Auswertung</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(REPORT_KINDS).map(([key, k]) => {
                const Icon = k.icon;
                const active = kind === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setKind(key)}
                    data-testid={`monthly-report-kind-${key}`}
                    className={`text-left rounded-md border p-3 transition-colors ${active
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-border hover:bg-accent/40'}`}
                  >
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <Icon className={`w-4 h-4 ${active ? 'text-indigo-400' : 'text-muted-foreground'}`} />
                      {k.label}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{k.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex gap-1">
            {[['all', 'Alle'], ['final', 'Vollständig genehmigt'], ['open', 'Nicht vollständig']].map(([key, label]) => (
              <Button
                key={key}
                type="button"
                size="sm"
                variant={filter === key ? 'default' : 'outline'}
                onClick={() => setFilter(key)}
                className={filter === key ? 'bg-indigo-500 text-white' : 'border-border text-muted-foreground'}
              >
                {label}
              </Button>
            ))}
          </div>
          <div className="relative sm:ml-auto sm:w-64">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name oder Personalnummer"
              className="pl-8 bg-input border-border"
            />
          </div>
        </div>

        <div className="rounded-md border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="monthly-report-persons">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="p-2 w-8">
                  <Checkbox checked={allVisibleSelected} onCheckedChange={toggleAllVisible} aria-label="Alle sichtbaren auswählen" />
                </th>
                <th className="p-2 text-left font-medium">Name</th>
                <th className="p-2 text-left font-medium">Pers.-Nr.</th>
                {weekHeads.map((kw) => (
                  <th key={kw} className="p-2 text-center font-medium">KW {kw}</th>
                ))}
                <th className="p-2 text-left font-medium">Nachweis</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4 + weekHeads.length} className="p-6 text-center text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Lade Personen …
                </td></tr>
              )}
              {!loading && visible.length === 0 && (
                <tr><td colSpan={4 + weekHeads.length} className="p-6 text-center text-muted-foreground">Keine Personen</td></tr>
              )}
              {!loading && visible.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-accent/30 cursor-pointer" onClick={() => toggle(p.id)}>
                  <td className="p-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} aria-label={`${p.name} auswählen`} />
                  </td>
                  <td className="p-2">
                    {p.name}
                    {!p.has_entries && <span className="ml-2 text-xs text-muted-foreground">(keine Einträge)</span>}
                  </td>
                  <td className="p-2 text-muted-foreground">{p.personnel_number || '–'}</td>
                  {p.weeks.map((w) => (
                    <td key={w.kw} className="p-2 text-center">
                      <span className={`inline-block px-1.5 py-0.5 rounded border text-xs ${TONE_CLASSES[w.tone] || ''}`} title={w.range}>
                        {w.label}
                      </span>
                    </td>
                  ))}
                  {p.weeks.length < weekHeads.length && Array.from({ length: weekHeads.length - p.weeks.length }).map((_, i) => (
                    <td key={`empty-${i}`} className="p-2" />
                  ))}
                  <td className="p-2">
                    <span className={p.final ? 'text-emerald-400' : 'text-amber-400'}>{p.final ? 'final' : 'vorläufig'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedList.length > 1 && (
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-muted-foreground">Ausgabe:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="monthly-output" checked={output === 'pdf'} onChange={() => setOutput('pdf')} />
              ein PDF, eine Seite je Person
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="monthly-output" checked={output === 'zip'} onChange={() => setOutput('zip')} />
              ZIP mit einem PDF je Person
            </label>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row sm:items-center gap-2">
          <p className="text-sm text-muted-foreground sm:mr-auto">
            {selectedList.length} {selectedList.length === 1 ? 'Person' : 'Personen'} ausgewählt
            {preliminaryCount > 0 && <> · davon {preliminaryCount} vorläufig</>}
          </p>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Schließen
          </Button>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={busy || loading || !selectedList.length}
            className="bg-[#E30613] hover:bg-[#c00510] text-white"
            data-testid="monthly-report-create"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            {selectedList.length <= 1 && 'PDF erstellen'}
            {selectedList.length > 1 && output === 'pdf' && `PDF erstellen (${selectedList.length} Personen)`}
            {selectedList.length > 1 && output === 'zip' && `ZIP erstellen (${selectedList.length} PDFs)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Für „Mein Arbeitszeitkonto“: eigene Auswertungen des angezeigten Monats
export function OwnMonthlyReportMenu({ api, year, month }) {
  const [busy, setBusy] = useState(false);
  const key = `${year}-${String(month).padStart(2, '0')}`;
  const run = async (kind) => {
    setBusy(true);
    await downloadMonthlyReport(api, { month: key, kind });
    setBusy(false);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" className="border-border text-foreground" disabled={busy} data-testid="own-monthly-report">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          PDF {monthLabel(key)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card border-border">
        {Object.entries(REPORT_KINDS).map(([kind, k]) => {
          const Icon = k.icon;
          return (
            <DropdownMenuItem key={kind} onClick={() => run(kind)} className="cursor-pointer">
              <Icon className="w-4 h-4 mr-2" />
              {k.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
